// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useHotelPartnerId } from './useHotelPartnerId';

// ── Types ───────────────────────────────────────────────────

export interface EventHeader {
  event_id: string;
  event_name: string;
  start_date: string | null;
  end_date: string | null;
  venue_name: string | null;
  venue_city: string | null;
  venue_state: string | null;
  booking_url: string | null;
  group_rate_expiry: string | null;
  assignment_id: string;
  hotel_count: number;
  rooms_contracted: number;
  rooms_booked: number;
  rooms_remaining: number;
  attrition_risk: boolean;
  hotel_exempt_radius_miles: number | null;
}

export interface TeamRow {
  registration_id: string;
  team_name: string;
  coach_name: string;
  coach_email: string;
  coach_phone: string;
  division: string;
  age_group: string;
  hotel_status: 'confirmed' | 'pending' | 'exempt' | 'cancelled' | 'completed' | 'unbooked';
  hotel_name: string | null;
  confirmation_number: string | null;
  check_in_date: string | null;
  check_out_date: string | null;
  last_emailed: string | null;
  booking_id: string | null;
  number_of_rooms: number | null;
  rate_per_night: number | null;
  booking_notes: string | null;
  notes_count: number;
  block_count: number;
  booking_source: string | null;
  rooms_picked_up: number;
  rooms_requested: number;
  pickup_pct: number;
  next_release_date: string | null;
  team_home_zip: string | null;
  distance_to_venue_miles: number | null;
  hotel_radius_exempt: boolean;
}

export interface HotelProperty {
  id: string;
  hotel_name: string;
  commission_pct: number | null;
  rebate_per_room: number | null;
  group_rate_per_night: number | null;
}

export interface TeamNote {
  id: string;
  note_text: string;
  created_by_name: string | null;
  created_at: string;
}

// ── Event Header ────────────────────────────────────────────

export function useEventHeader(eventId: string | undefined) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({
    queryKey: ['hotel-event-header', eventId, partnerId],
    queryFn: async () => {
      // Get event
      const { data: ev, error: eErr } = await supabase
        .from('events')
        .select('id, name, start_date, end_date, venue_name, venue_city, venue_state, hotel_exempt_radius_miles')
        .eq('id', eventId!)
        .single();
      if (eErr) throw eErr;

      // Get assignment
      const { data: assign, error: aErr } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('id, booking_url, group_rate_expiry')
        .eq('event_id', eventId!)
        .eq('hotel_partner_user_id', partnerId)
        .single();
      if (aErr) throw aErr;

      // Hotel count
      const { count: hotelCount } = await (supabase as any)
        .from('hotel_properties')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId!);

      // Compliance
      const { data: comp } = await (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('rooms_contracted, rooms_booked, rooms_remaining, attrition_risk')
        .eq('event_id', eventId!)
        .eq('hotel_partner_user_id', partnerId)
        .maybeSingle();

      return {
        event_id: ev.id,
        event_name: ev.name,
        start_date: ev.start_date,
        end_date: ev.end_date,
        venue_name: ev.venue_name,
        venue_city: ev.venue_city,
        venue_state: ev.venue_state,
        booking_url: assign.booking_url,
        group_rate_expiry: assign.group_rate_expiry,
        assignment_id: assign.id,
        hotel_count: hotelCount ?? 0,
        rooms_contracted: Number(comp?.rooms_contracted) || 0,
        rooms_booked: Number(comp?.rooms_booked) || 0,
        rooms_remaining: Number(comp?.rooms_remaining) || 0,
        attrition_risk: comp?.attrition_risk ?? false,
        hotel_exempt_radius_miles: (ev as any).hotel_exempt_radius_miles ?? null,
      } as EventHeader;
    },
    enabled: !!eventId && !!partnerId,
  });
}

// ── Update assignment fields ────────────────────────────────

export function useUpdateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ assignmentId, field, value }: { assignmentId: string; field: string; value: string | null }) => {
      const { error } = await (supabase as any)
        .from('hotel_event_assignments')
        .update({ [field]: value })
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-event-header'] });
    },
  });
}

// ── Team List (from the v_hotel_team_tracker view) ──────────

export function useTeamList(eventId: string | undefined) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({
    queryKey: ['hotel-team-list', eventId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('v_hotel_team_tracker')
        .select('*')
        .eq('event_id', eventId!);
      if (error) throw error;

      // The tracker view doesn't carry the local-team exemption fields, so
      // read them from event_registrations_v2 by registration_id.
      const regIds = (data ?? []).map((r: any) => r.registration_id).filter(Boolean);
      const exemptMap = new Map<string, { team_home_zip: string | null; distance_to_venue_miles: number | null; hotel_radius_exempt: boolean }>();
      if (regIds.length > 0) {
        const { data: regs } = await (supabase as any)
          .from('event_registrations_v2')
          .select('id, team_home_zip, distance_to_venue_miles, hotel_radius_exempt')
          .in('id', regIds);
        for (const r of regs ?? []) {
          exemptMap.set(r.id, {
            team_home_zip: r.team_home_zip ?? null,
            distance_to_venue_miles: r.distance_to_venue_miles ?? null,
            hotel_radius_exempt: !!r.hotel_radius_exempt,
          });
        }
      }

      return (data ?? []).map((r): TeamRow => {
        const s = (r.booking_status || '').toLowerCase();
        let hotel_status: TeamRow['hotel_status'] = 'unbooked';
        if (s === 'confirmed' || s === 'completed') hotel_status = 'confirmed';
        else if (s === 'exempt') hotel_status = 'exempt';
        else if (s === 'pending') hotel_status = 'pending';
        else if (s === 'cancelled') hotel_status = 'cancelled';

        return {
          registration_id: r.registration_id,
          team_name: r.team_name || 'Unknown Team',
          coach_name: r.coach_name || '',
          coach_email: r.coach_email || '',
          coach_phone: r.coach_phone || '',
          division: r.division || '',
          age_group: r.age_group || '',
          hotel_status,
          hotel_name: r.hotel_name ?? null,
          confirmation_number: r.confirmation_number ?? null,
          check_in_date: r.check_in_date ?? null,
          check_out_date: r.check_out_date ?? null,
          last_emailed: r.last_outreach_at ?? null,
          booking_id: r.booking_id ?? null,
          number_of_rooms: r.number_of_rooms ?? null,
          rate_per_night: r.rate_per_night ?? null,
          booking_notes: r.booking_notes ?? null,
          notes_count: Number(r.notes_count) || 0,
          block_count: Number(r.block_count) || 0,
          booking_source: r.booking_source ?? null,
          rooms_picked_up: Number(r.rooms_picked_up) || 0,
          rooms_requested: Number(r.rooms_requested) || 0,
          pickup_pct: Number(r.pickup_pct) || 0,
          next_release_date: r.next_release_date ?? null,
          team_home_zip: exemptMap.get(r.registration_id)?.team_home_zip ?? null,
          distance_to_venue_miles: exemptMap.get(r.registration_id)?.distance_to_venue_miles ?? null,
          hotel_radius_exempt: exemptMap.get(r.registration_id)?.hotel_radius_exempt ?? false,
        };
      });
    },
    enabled: !!eventId && !!partnerId,
  });
}

// ── Hotel Properties for event ──────────────────────────────

export function useHotelProperties(eventId: string | undefined) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({
    queryKey: ['hotel-properties', eventId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_properties')
        .select('id, hotel_name, commission_pct, rebate_per_room, group_rate_per_night')
        .eq('event_id', eventId!)
        .order('hotel_name');
      if (error) throw error;
      return (data ?? []) as HotelProperty[];
    },
    enabled: !!eventId && !!partnerId,
  });
}

// ── Add Booking ─────────────────────────────────────────────
// room_nights / gross_revenue / commission_earned / rebate_earned are
// computed by a database trigger — never send them from the client.

export function useAddBooking(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      registration_id: string;
      event_id: string;
      hotel_property_id: string | null;
      hotel_name: string;
      confirmation_number: string;
      check_in_date: string;
      check_out_date: string;
      number_of_rooms: number;
      rate_per_night: number;
      status: string;
      notes: string;
      commission_pct: number | null;
      rebate_per_room: number | null;
      entered_by: string;
      coach_email: string;
      team_name: string;
    }) => {
      const { error } = await (supabase as any).from('hotel_bookings').insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-team-list', eventId] });
      qc.invalidateQueries({ queryKey: ['hotel-event-header'] });
    },
  });
}

// ── Mark Exempt ─────────────────────────────────────────────

export function useMarkExempt(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ registrationIds, eventIdVal, userId }: { registrationIds: string[]; eventIdVal: string; userId: string }) => {
      for (const regId of registrationIds) {
        // Check if booking exists
        const { data: existing } = await (supabase as any)
          .from('hotel_bookings')
          .select('id')
          .eq('registration_id', regId)
          .eq('event_id', eventIdVal)
          .maybeSingle();

        if (existing) {
          await (supabase as any).from('hotel_bookings').update({ status: 'exempt' }).eq('id', existing.id);
        } else {
          await (supabase as any).from('hotel_bookings').insert({
            registration_id: regId,
            event_id: eventIdVal,
            status: 'exempt',
            entered_by: userId,
            hotel_name: 'N/A',
          });
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-team-list', eventId] });
      qc.invalidateQueries({ queryKey: ['hotel-event-header'] });
    },
  });
}

// ── Team home ZIP ───────────────────────────────────────────
// Saving a ZIP lets the database recompute distance and the radius exemption.

export function useUpdateTeamZip(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ registrationId, zip }: { registrationId: string; zip: string }) => {
      const { error } = await (supabase as any)
        .from('event_registrations_v2')
        .update({ team_home_zip: zip })
        .eq('id', registrationId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-team-list', eventId] });
    },
  });
}

// ── Team Notes ──────────────────────────────────────────────

export function useTeamNotes(eventId: string | undefined, registrationId: string | undefined) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({
    queryKey: ['hotel-team-notes', eventId, registrationId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_team_notes')
        .select('id, note_text, created_by_name, created_at')
        .eq('event_id', eventId!)
        .eq('registration_id', registrationId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as TeamNote[];
    },
    enabled: !!eventId && !!registrationId && !!partnerId,
  });
}

export function useAddTeamNote(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      event_id: string;
      registration_id: string;
      team_name: string;
      coach_email: string;
      note_text: string;
      created_by: string;
      created_by_name: string;
    }) => {
      const { error } = await (supabase as any).from('hotel_team_notes').insert(payload);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['hotel-team-notes', eventId, vars.registration_id] });
      qc.invalidateQueries({ queryKey: ['hotel-team-list', eventId] });
    },
  });
}

// ── Update Booking ──────────────────────────────────────────
// room_nights / gross_revenue are trigger-computed — not sent.

export function useUpdateBooking(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      bookingId: string;
      hotel_name: string;
      confirmation_number: string;
      check_in_date: string;
      check_out_date: string;
      number_of_rooms: number;
      rate_per_night: number;
      status: string;
      notes: string;
    }) => {
      const { bookingId, ...updates } = payload;
      const { error } = await (supabase as any)
        .from('hotel_bookings')
        .update(updates)
        .eq('id', bookingId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-team-list', eventId] });
      qc.invalidateQueries({ queryKey: ['hotel-event-header'] });
    },
  });
}
